import type { ApplicationVerificationSnapshotErrorDto } from '../../api/admin';
import {
  reviewerSafeEmployerVerificationError,
  toReviewerSafeError,
  type ReviewerSafeError,
} from '../reviewerSafeError';

export type EmployerReviewErrorClass =
  | 'snapshot-incomplete'
  | 'verification-unavailable'
  | 'footprint-unavailable'
  | 'verification-detail-unavailable'
  | 'unknown';

export type EmployerPanelLoadState = 'loading' | 'ok' | 'degraded' | 'failed';

export type EmployerReviewPanelIssue = {
  class: EmployerReviewErrorClass;
  message: string;
  correlationId?: string;
  retryable: boolean;
};

const ISSUE_COPY: Record<
  EmployerReviewErrorClass,
  { message: string; retryable: boolean }
> = {
  'snapshot-incomplete': {
    message:
      'Some employment data couldn’t be loaded from the application journey. You can still review what the customer declared below.',
    retryable: false,
  },
  'verification-unavailable': {
    message: 'Employment verification is temporarily unavailable. Try again in a moment.',
    retryable: true,
  },
  'footprint-unavailable': {
    message: 'Digital footprint couldn’t be loaded. Formal verification may still be available.',
    retryable: true,
  },
  'verification-detail-unavailable': {
    message: 'Full verification details couldn’t be loaded. You can retry to continue the review.',
    retryable: true,
  },
  unknown: {
    message: 'Employment review is unavailable right now. Retry, or contact platform ops with the reference below.',
    retryable: true,
  },
};

export type EmployerReviewQuerySlice = {
  isPending: boolean;
  isFetching: boolean;
  isError: boolean;
  isSuccess: boolean;
  error?: unknown;
};

export type DeriveEmployerReviewPanelStateInput = {
  snapshotItemsCount: number;
  scopeErrors: ApplicationVerificationSnapshotErrorDto[];
  directListQ: EmployerReviewQuerySlice;
  footprintQ: EmployerReviewQuerySlice;
  latestDetailQ: EmployerReviewQuerySlice;
  sortedItemsCount: number;
  hasLatestVerificationId: boolean;
  usedDirectList: boolean;
};

export type EmployerReviewPanelState = {
  loadState: EmployerPanelLoadState;
  issues: EmployerReviewPanelIssue[];
  headline: string;
  detail: string;
  correlationId?: string;
  retryable: boolean;
  showInitialLoading: boolean;
  canShowEmptyState: boolean;
  canShowVerificationContent: boolean;
  showDetailDegradedHint: boolean;
};

function issueFromClass(
  cls: EmployerReviewErrorClass,
  correlationId?: string,
): EmployerReviewPanelIssue {
  const copy = ISSUE_COPY[cls];
  return {
    class: cls,
    message: copy.message,
    correlationId,
    retryable: copy.retryable,
  };
}

function pickCorrelationId(...candidates: Array<string | undefined>): string | undefined {
  for (const id of candidates) {
    if (typeof id === 'string' && id.trim()) return id.trim();
  }
  return undefined;
}

function safeErrorCorrelation(err: unknown): string | undefined {
  return reviewerSafeEmployerVerificationError(err).correlationId;
}

function dedupeIssues(issues: EmployerReviewPanelIssue[]): EmployerReviewPanelIssue[] {
  const seen = new Set<EmployerReviewErrorClass>();
  const out: EmployerReviewPanelIssue[] = [];
  for (const issue of issues) {
    if (seen.has(issue.class)) continue;
    seen.add(issue.class);
    out.push(issue);
  }
  return out;
}

function aggregateCopy(issues: EmployerReviewPanelIssue[], loadState: EmployerPanelLoadState): {
  headline: string;
  detail: string;
} {
  if (loadState === 'failed') {
    if (issues.length === 1) {
      return {
        headline: 'Employment review is temporarily unavailable.',
        detail: issues[0]!.message,
      };
    }
    return {
      headline: 'Employment review is temporarily unavailable.',
      detail: 'Some employment evidence could not be loaded. Try again in a moment.',
    };
  }

  if (issues.length === 1) {
    const only = issues[0]!;
    if (only.class === 'footprint-unavailable') {
      return {
        headline: 'Digital footprint couldn’t be loaded.',
        detail: 'Employment verification remains available.',
      };
    }
    if (only.class === 'verification-detail-unavailable') {
      return {
        headline: 'Full verification details couldn’t be loaded.',
        detail: only.message,
      };
    }
    if (only.class === 'snapshot-incomplete') {
      return {
        headline: 'Some employment journey data is incomplete.',
        detail: only.message,
      };
    }
  }

  return {
    headline: 'Some employment evidence couldn’t be loaded.',
    detail: 'You can continue with the information that is available, or retry.',
  };
}

export function deriveEmployerReviewPanelState(
  input: DeriveEmployerReviewPanelStateInput,
): EmployerReviewPanelState {
  const {
    snapshotItemsCount,
    scopeErrors,
    directListQ,
    footprintQ,
    latestDetailQ,
    sortedItemsCount,
    hasLatestVerificationId,
    usedDirectList,
  } = input;

  const hasSnapshotList = snapshotItemsCount > 0;
  const listSucceeded = directListQ.isSuccess || hasSnapshotList;
  const listFailed = directListQ.isError && !hasSnapshotList;
  const listLoading = !hasSnapshotList && directListQ.isPending && !directListQ.isError;

  const hasVerificationRecords = sortedItemsCount > 0;
  const footprintFailed = footprintQ.isError;
  const detailFailed = hasLatestVerificationId && latestDetailQ.isError;
  const snapshotIncomplete = scopeErrors.length > 0 && !usedDirectList;

  const issues: EmployerReviewPanelIssue[] = [];

  if (listFailed) {
    issues.push(
      issueFromClass('verification-unavailable', safeErrorCorrelation(directListQ.error)),
    );
  }

  if (snapshotIncomplete) {
    issues.push(issueFromClass('snapshot-incomplete'));
  }

  if (footprintFailed) {
    issues.push(
      issueFromClass('footprint-unavailable', safeErrorCorrelation(footprintQ.error)),
    );
  }

  if (detailFailed) {
    issues.push(
      issueFromClass(
        'verification-detail-unavailable',
        safeErrorCorrelation(latestDetailQ.error),
      ),
    );
  }

  const deduped = dedupeIssues(issues);
  const correlationId = pickCorrelationId(...deduped.map((i) => i.correlationId));

  let loadState: EmployerPanelLoadState = 'ok';
  if (listLoading) {
    loadState = 'loading';
  } else if (listFailed && !hasVerificationRecords) {
    loadState = 'failed';
  } else if (deduped.length > 0) {
    loadState = 'degraded';
  }

  const { headline, detail } =
    deduped.length > 0
      ? aggregateCopy(deduped, loadState)
      : { headline: '', detail: '' };

  const canShowEmptyState =
    loadState !== 'failed' &&
    !listLoading &&
    !listFailed &&
    listSucceeded &&
    sortedItemsCount === 0;

  const canShowVerificationContent =
    hasVerificationRecords && loadState !== 'failed';

  return {
    loadState,
    issues: deduped,
    headline,
    detail,
    correlationId,
    retryable: deduped.some((i) => i.retryable),
    showInitialLoading: listLoading && loadState !== 'failed',
    canShowEmptyState,
    canShowVerificationContent,
    showDetailDegradedHint: detailFailed && hasVerificationRecords,
  };
}

/** Build a safe error view for section-level render boundaries. */
export function employerReviewSectionSafeError(err: unknown): ReviewerSafeError {
  return toReviewerSafeError({ err, fallbackCode: 'INTERNAL' });
}
